import { ControlConfigInfo, isRoomDryingProgram, calculateIndexFromSlotNumber } from "./ComBox";
import { IresDeviceResponseReader } from "@/utils/IresDeviceResponseReader";
import * as base64Buffer from "base64-arraybuffer";
import { hasCapabilityForVersion, DeviceCapability } from "./DeviceCapability";

export enum ComBoxSensorUsage {
  NOT_USED,
  PROGRAM,
  MEASUREMENT,
  REFERENCE,
}

export function formatSensorUsage(vue: Vue, sensorUsage: ComBoxSensorUsage) {
  switch (sensorUsage) {
    case ComBoxSensorUsage.NOT_USED:
      return "";
    case ComBoxSensorUsage.PROGRAM:
      return vue.$t("comBoxDetail.sensorUsage.program").toString();
    case ComBoxSensorUsage.MEASUREMENT:
      return vue.$t("comBoxDetail.sensorUsage.measurement").toString();
    case ComBoxSensorUsage.REFERENCE:
      return vue.$t("comBoxDetail.sensorUsage.reference").toString();
  }
}

export interface ControlConfig {
  socket1Mode: SocketMode;
  socket2Mode: SocketMode;
  dryingMode: DryingMode;
  dryingProgram: number;
  sensorProgram: number;
  sensorUsage: ComBoxSensorUsage[];
  nightModeActive: boolean;
  nightModeAffectsAllSockets: boolean;
  nightModeStartHour: number;
  nightModeStartMinute: number;
  nightModeEndHour: number;
  nightModeEndMinute: number;
  nightModeActiveDays: boolean[];
}

export enum SocketMode {
  OFF,
  ON,
  PROGRAM,
}

export function getDefaultSocketModesForDryingProgram(dryingProgram: number) {
  switch (dryingProgram) {
    case 0:
    case 1:
    case 2: {
      return {
        socket1Mode: SocketMode.PROGRAM,
        socket2Mode: SocketMode.PROGRAM,
      };
    }
    case 3: {
      return {
        socket1Mode: SocketMode.ON,
        socket2Mode: SocketMode.ON,
      };
    }
    case 4: {
      return {
        socket1Mode: SocketMode.PROGRAM,
        socket2Mode: SocketMode.ON,
      };
    }
  }

  return {
    socket1Mode: SocketMode.ON,
    socket2Mode: SocketMode.ON,
  };
}

export function getSocketModeFromFlag(flag: number): SocketMode {
  return flag === 0
    ? SocketMode.OFF
    : flag === 1
    ? SocketMode.ON
    : SocketMode.PROGRAM;
}

export function formatSocketMode(vue: Vue, socketMode: SocketMode) {
  switch (socketMode) {
    case SocketMode.PROGRAM: {
      return vue
        .$t("comBoxDetail.controlConfig.socketModeOptions.program")
        .toString();
    }
    case SocketMode.ON: {
      return vue
        .$t("comBoxDetail.controlConfig.socketModeOptions.on")
        .toString();
    }
    case SocketMode.OFF: {
      return vue
        .$t("comBoxDetail.controlConfig.socketModeOptions.off")
        .toString();
    }
  }
}

export enum DryingMode {
  NEGATIVE_PRESSURE,
  POSITIVE_PRESSURE,
  PUSH_PULL,
}

export function getDryingModeFromFlag(flag: number) {
  return flag === 0
    ? DryingMode.NEGATIVE_PRESSURE
    : flag === 1
    ? DryingMode.POSITIVE_PRESSURE
    : DryingMode.PUSH_PULL;
}

export function formatDryingMode(vue: Vue, dryingMode: DryingMode) {
  switch (dryingMode) {
    case DryingMode.NEGATIVE_PRESSURE: {
      return vue
        .$t("comBoxDetail.controlConfig.dryingModeOptions.negativePressure")
        .toString();
    }
    case DryingMode.POSITIVE_PRESSURE: {
      return vue
        .$t("comBoxDetail.controlConfig.dryingModeOptions.positivePressure")
        .toString();
    }
    case DryingMode.PUSH_PULL: {
      return vue
        .$t("comBoxDetail.controlConfig.dryingModeOptions.pushPull")
        .toString();
    }
  }
}

export function parseControlConfig(
  iresDeviceResponseReader: IresDeviceResponseReader,
  offsetToUtc: number,
  firmwareVersion: string,
  serialNumber: string
): ControlConfig {
  const socketMask = iresDeviceResponseReader.readUint8();

  const socket1Mode = getSocketModeFromFlag(socketMask % 4);
  const socket2Mode = getSocketModeFromFlag((socketMask >>> 2) % 4);

  const dryingModeFlag = iresDeviceResponseReader.readUint8();
  const dryingMode = getDryingModeFromFlag(dryingModeFlag);

  const dryingAndSensorProgram = iresDeviceResponseReader.readUint8();
  // The firmware's PrgSelect: drying program in the low nibble, sensor program in the high nibble.
  const dryingProgram = dryingAndSensorProgram & 0x0f;
  const sensorProgram = dryingAndSensorProgram >>> 4;

  let sensorUsage: ComBoxSensorUsage[] = [];

  if (hasCapabilityForVersion(serialNumber, firmwareVersion, DeviceCapability.SENSOR_USAGE_INFO)) {
    let bitField = iresDeviceResponseReader.readUint8();

    for (let i = 0; i < 4; i++) {
      const flag = bitField % 4;

      let usage = ComBoxSensorUsage.NOT_USED;

      if (flag === 1) {
        usage = ComBoxSensorUsage.MEASUREMENT;
      } else if (flag === 2) {
        usage = ComBoxSensorUsage.PROGRAM;
      } else if (flag === 3) {
        usage = ComBoxSensorUsage.REFERENCE;
      }

      sensorUsage.push(usage);

      bitField = bitField >>> 2;
    }

    if (isRoomDryingProgram(dryingProgram)) {
      sensorUsage.push(ComBoxSensorUsage.PROGRAM);
    } else if (
      sensorUsage.find((value) => value === ComBoxSensorUsage.REFERENCE)
    ) {
      sensorUsage.push(ComBoxSensorUsage.MEASUREMENT);
    } else {
      sensorUsage.push(ComBoxSensorUsage.REFERENCE);
    }
  } else {
    const selectedSensors = iresDeviceResponseReader.readBitField(5);

    sensorUsage = selectedSensors.map((isSelected, index) =>
      isSelected
        ? ComBoxSensorUsage.PROGRAM
          : index == 4
          ? ComBoxSensorUsage.REFERENCE
        : ComBoxSensorUsage.MEASUREMENT
    );
  }

  const nightModeFlags = iresDeviceResponseReader.readUint8();
  const nightModeActive = nightModeFlags % 2 !== 0;

  const nightModeAffectsAllSockets = (nightModeFlags >>> 1) % 2 !== 0;

  // Night pause hours in CONTROL_CONFIG are stored in UTC.
  // The UTC offset is applied here to convert from the raw device value (local time)
  // to UTC for consistent storage and comparison.
  const nightModeStartHourWithoutUtcOffset = iresDeviceResponseReader.readUint8();
  const nightModeStartHour =
    (offsetToUtc + 240 + nightModeStartHourWithoutUtcOffset) % 24;

  const nightModeStartMinute = iresDeviceResponseReader.readUint8();

  const nightModeEndHourWithoutUtcOffset = iresDeviceResponseReader.readUint8();
  const nightModeEndHour =
    (offsetToUtc + 240 + nightModeEndHourWithoutUtcOffset) % 24;

  const nightModeEndMinute = iresDeviceResponseReader.readUint8();
  const nightModeActiveDays = iresDeviceResponseReader.readBitField(7);

  /**
   * ==================== FIRMWARE v4.0.0+ CHANGES ====================
   * This section handles parsing of sensor usage for firmware version 4.0.0 and above.
   * - Reads 4 bytes (total 32 bits) representing sensor usage information.
   * - Each sensor (8 total) uses 2 bits to describe its usage type.
   * - The room sensor usage also uses 2 bits but its information is located last in the bitfield.
   * - Room sensor usage is inserted into the `sensorUsage` array at index 4 (after the first 4 sensors).
   * 
   * Parsing order:
   *   0–3 → First 4 sensors
   *   5–8 → Next 4 sensors
   *   4   → Room sensor (added last but inserted in middle)
   */
  if (hasCapabilityForVersion(serialNumber, firmwareVersion, DeviceCapability.EXTENDED_SENSORS)) {
    const bytesFollowing = iresDeviceResponseReader.readUint8();

    if (bytesFollowing === 4) {
      let bitField = 0;
      for (let i = 0; i < 4; i++) {
        bitField |= iresDeviceResponseReader.readUint8() << (i * 8);
      }

      const mapFlagToUsage = (flag: number): ComBoxSensorUsage => {
        if (flag === 1) return ComBoxSensorUsage.MEASUREMENT;
        if (flag === 2) return ComBoxSensorUsage.PROGRAM;
        if (flag === 3) return ComBoxSensorUsage.REFERENCE;
        return ComBoxSensorUsage.NOT_USED;
      };

      // Parse all 9 slots (1-9), using calculateIndexFromSlotNumber for each
      for (let slotNo = 1; slotNo <= 9; slotNo++) {
        const flag = bitField % 4;
        const idx = calculateIndexFromSlotNumber(slotNo);
        sensorUsage[idx] = mapFlagToUsage(flag);
        bitField = bitField >>> 2;
      }
    }
  }

  return {
    socket1Mode: socket1Mode,
    socket2Mode: socket2Mode,
    dryingMode: dryingMode,
    dryingProgram: dryingProgram,
    sensorProgram: sensorProgram,
    sensorUsage: sensorUsage,
    nightModeActive: nightModeActive,
    nightModeAffectsAllSockets: nightModeAffectsAllSockets,
    nightModeStartHour: nightModeStartHour,
    nightModeStartMinute: nightModeStartMinute,
    nightModeEndHour: nightModeEndHour,
    nightModeEndMinute: nightModeEndMinute,
    nightModeActiveDays: nightModeActiveDays,
  };
}

/**
 * The start / change dialog's starting config when nothing readable is stored anywhere: an idle
 * box with an erased or client-config record, on a client without saved HyDry device settings.
 * Without it the dialog never displayed a config, so its program picker kept the Box-only
 * 0 / 1 / 2 list, even for a Mini. Starts on the given program (the client's default the
 * dialog may offer, see chooseStartDryingProgram), with that program's sockets and night mode off.
 */
export function getFallbackControlConfig(
  dryingProgram: number,
  sensorUsage: ComBoxSensorUsage[]
): ControlConfig {
  return {
    ...getDefaultSocketModesForDryingProgram(dryingProgram),
    dryingMode: DryingMode.NEGATIVE_PRESSURE,
    dryingProgram: dryingProgram,
    sensorProgram: 0,
    sensorUsage: sensorUsage.slice(),
    nightModeActive: false,
    nightModeAffectsAllSockets: true,
    nightModeStartHour: 22,
    nightModeStartMinute: 0,
    nightModeEndHour: 6,
    nightModeEndMinute: 0,
    nightModeActiveDays: Array(7).fill(false),
  };
}

/**
 * Byte lengths a stored control config (ID 32) has in production: 10 without the CRC, 11 with it,
 * and 16 with the extended sensor block (firmware 4.0.0+) and the CRC.
 */
const CONTROL_CONFIG_RECORD_LENGTHS = [10, 11, 16];

/**
 * Whether a stored `currentControlConfig` / `defaultControlConfig` really is a control config (ID 32).
 *
 * The same fields also hold other records: the box's client config (ID 16, 51 bytes, starting with
 * its name "Client"), that record's 15-byte tail, a 458-byte drying programs config, and erased
 * records (all 0xFF). Parsed as a control config they read as Push-Pull, P1 and similar nonsense,
 * so callers fall back to a default instead.
 */
export function isControlConfigRecord(controlConfigInfo: ControlConfigInfo): boolean {
  if (!controlConfigInfo?.dataBase64) {
    return false;
  }

  const data = new Uint8Array(base64Buffer.decode(controlConfigInfo.dataBase64));

  // Byte 0 packs the two 2-bit socket modes, so a socket mask above 0x0F is not one.
  return CONTROL_CONFIG_RECORD_LENGTHS.includes(data.length) && data[0] <= 0x0f;
}

/**
 * Decodes a base64 control config payload and parses it into a {@link ControlConfig}.
 * Expects raw CONTROL_CONFIG (0x20) format in `dataBase64` — socketMask at byte 0.
 * Check stored records with {@link isControlConfigRecord} first.
 */
export function getControlConfig(
  controlConfigInfo: ControlConfigInfo,
  offsetToUtc: number,
  serialNumber: string
): ControlConfig {
  const data = new Uint8Array(base64Buffer.decode(controlConfigInfo.dataBase64));

  const iresDeviceResponseReader = new IresDeviceResponseReader(data);

  return parseControlConfig(
    iresDeviceResponseReader,
    offsetToUtc,
    controlConfigInfo.comBoxFirmwareVersion,
    serialNumber
  );
}
